
import React, { Fragment, useEffect, useRef, useState } from "react";
import { CiSearch } from "react-icons/ci";
import { FaCheck } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import AppIcon from "./AppIcon";
import useModal from "../../hooks/useModal";
import {
  getSearch,
  isArray,
  kindOf,
  saveSearch,
  showToast,
} from "../../lib/utility";
import { middlewareCreate } from "../../redux/store";
import { saveFilter } from "../Report/ReportSlice";
import FormEnter from "./FormEnter";
import SaveFilter from "./SaveFilter";
import Tooltip from "./Tooltip";

const TableWithHeader = ({
  name,
  setShowFilterModal,
  search,
  serverSearch = false,
  customChildren,
  charts,
  customChildrenPlacement = "first",
  showSavedFilter,
  handleSavedFilterClick,
  children,
  isPinned = false,
  className = "",
  style = {},
  tableMinHeight,
  grow = true,
  searchDisabled = false,
}) => {
  const { t } = useTranslation(["common", "social"]);
  let showSearch = !!search;
  search = search ?? {};
  let {
    searchVal,
    setSearchVal,
    handleSearch,
    saveSearchKey,
    saveFilterKey,
    filterData,
    refreshFilter,
  } = search;

  const [filterMobActive, setFilterMobActive] = useState(false);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState("");
  const [isSavedFilterOpen, setIsSavedFilterOpen] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const [savedSearch, setSavedSearch] = useState(false);

  const savedFilterRef = useRef(null);
  const containerRef = useRef(null);
  const [isTableEmpty, setIsTableEmpty] = useState(false);

  const inputRef = useRef();
  const dispatch = useDispatch();
  const { clickRef, contRef, showModal, handleOpen, handleClose } = useModal();
  const { x, y, right, height, width } =
    clickRef.current?.getBoundingClientRect() ?? {};

  const handleFilterActive = (e) => {
    if (!filterMobActive && !searchVal && e?.type === "click") {
      e?.preventDefault();
      inputRef.current?.focus();
      setFilterMobActive(true);
      return;
    }

    if (kindOf(handleSearch, "function")) {
      e?.preventDefault && e.preventDefault();
      const searchEvent = {
        ...e,
        type: "submit",
        key: "Enter",
        keyCode: 13,
        which: 13,
        preventDefault: () => e?.preventDefault && e.preventDefault(),
        target: inputRef.current || e?.target,
      };
      handleSearch(searchEvent);
    }
  };

  const handleBlur = (e) => {
    setFilterMobActive(false);
  };

  const handleSaveClick = (saveText) => {
    if (saveText === "") {
      showToast("Filter name can not be empty",
        "warning"
      );
      return;
    }

    dispatch(
      saveFilter({
        recentFilterName: saveText,
        purpose: saveFilterKey,
        createdOn: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        specialAttributes: { ...filterData, searchValue: searchVal },
      })
    ).then((action) =>
      middlewareCreate(
        action,
        "Save Filter",
        () => {
          refreshFilter && refreshFilter();
          handleClose();
          setSavedSearch(true);
          setTimeout(() => {
            setSavedSearch(false);
          }, 2000);
        },
        undefined,
        {
          modal: handleClose,
        }
      )
    );
  };

  useEffect(() => {
    if (!isSavedFilterOpen) return;
    const handleOutsideClick = (e) => {
      if (
        savedFilterRef.current &&
        !savedFilterRef.current.contains(e.target)
      ) {
        setIsSavedFilterOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsSavedFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSavedFilterOpen]);

  // Tables render the shared empty-state component when they have no rows;
  // other layouts (cards, custom lists) never match, so search stays enabled there.
  useEffect(() => {
    const node = containerRef.current;
    if (!node || !showSearch) return;

    let frame = null;
    const checkEmpty = () => {
      frame = null;
      const hasEmptyState = !!node.querySelector(".dashboard-graph-empty-state");
      const hasRows = !!node.querySelector(".rdt_TableRow, tbody tr");
      setIsTableEmpty(hasEmptyState && !hasRows);
    };
    const scheduleCheck = () => {
      if (frame === null) frame = requestAnimationFrame(checkEmpty);
    };

    checkEmpty();
    const observer = new MutationObserver(scheduleCheck);
    observer.observe(node, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [showSearch]);

  const isSearchDisabled = searchDisabled || (isTableEmpty && !searchVal);

  const handleToggleSavedFilter = () => {
    setIsSavedFilterOpen((prev) => {
      const next = !prev;
      if (next) {
        if (savedFilterRef.current) {
          const rect = savedFilterRef.current.getBoundingClientRect();
          const spaceBelow = window.innerHeight - rect.bottom;
          setOpenAbove(spaceBelow < 220 && rect.top > 220);
        }
        if (refreshFilter) {
          refreshFilter();
        }
      }
      return next;
    });
  };

  const handleSelectSavedFilter = (id) => {
    let specialAttributes = (Array.isArray(showSavedFilter) ? showSavedFilter : []).find(
      (sf) => String(sf.id) === String(id)
    )?.specialAttributes;

    setSelectedSavedFilter(id);
    if (specialAttributes && handleSavedFilterClick) {
      handleSavedFilterClick(specialAttributes);
    }
    setIsSavedFilterOpen(false);
  };

  const selectedFilterObj = (Array.isArray(showSavedFilter) ? showSavedFilter : []).find(
    (f) => String(f.id) === String(selectedSavedFilter)
  );
  const selectedFilterLabel = selectedFilterObj
    ? selectedFilterObj.recentFilterName
    : t("commonUi.savedFilters", {
        ns: "common",
        defaultValue: "-Saved Filters-",
      });

  const customChildrenStyle = {
    gap: 8,
    flexShrink: 0,
  };

  return (
    <>
      {charts && (
        <div className="charts | row mb-4 g-3">
          {charts.map((chart, index) => (
            <div className="col-12 col-md-6" key={index}>
              {chart}
            </div>
          ))}
        </div>
      )}
      <div
        ref={containerRef}
        className={
          "commonTableHeader common-table-layout d-flex" +
          (grow ? " flex-grow-1" : "") +
          (className ? " " + className : "")
        }
        style={{ flexDirection: "column", minHeight: tableMinHeight, ...style }}
      >
        <div
          className={
            "d-flex align-items-center flex-wrap commonTable" +
            (name ? " justify-content-between" : " noName justify-content-end")
          }
        >
          {name && (
            <div className="d-flex align-items-center" style={{ gap: 6 }}>
              <p className="name mb-0">{name}</p>
            </div>
          )}
          <div
            className="d-flex align-items-center table-header-controls"
            style={{ gap: 8, minWidth: 0, flex: "1 1 auto" }}
          >
            {customChildrenPlacement === "first" && (
              <div
                className="custom | d-flex align-items-center"
                style={customChildrenStyle}
              >
                {isArray(customChildren)
                  ? customChildren.map((child, index) => (
                      <Fragment key={index}>{child}</Fragment>
                    ))
                  : customChildren}
              </div>
            )}
            {setShowFilterModal && (
              <button
                className="filterBtn"
                onClick={() => setShowFilterModal(true)}
              >
                <AppIcon name="filter" size={16} className="noShrink" alt="" />
                <span>
                  {t("commonUi.filter", { ns: "common", defaultValue: "Filter" })}
                </span>
              </button>
            )}
            {saveFilterKey && (
              <div
                className="dropdown savedFilter"
                ref={savedFilterRef}
              >
                <button
                  type="button"
                  className={
                    "form-select saved-filter-trigger text-start" +
                    (name ? " text-white" : "") +
                    (isSavedFilterOpen ? " is-open" : "")
                  }
                  onClick={handleToggleSavedFilter}
                  style={{ borderRadius: 8 }}
                  aria-expanded={isSavedFilterOpen}
                >
                  <Tooltip
                    content={selectedFilterLabel}
                    placement="bottom"
                    onlyWhenTruncated
                    ellipsis
                    disabled={isSavedFilterOpen}
                    className="w-100"
                  >
                    {selectedFilterLabel}
                  </Tooltip>
                </button>

                {isSavedFilterOpen && (
                  <div
                    className="dropdown-menu saved-filter-menu show"
                    role="listbox"
                    style={
                      openAbove
                        ? { bottom: "calc(100% + 4px)", top: "auto" }
                        : { top: "calc(100% + 4px)", bottom: "auto" }
                    }
                  >
                    <div
                      className={
                        "saved-filter-item" +
                        (selectedSavedFilter === "" ? " active" : "")
                      }
                      onClick={() => handleSelectSavedFilter("")}
                    >
                      <span className="saved-filter-item-text">
                        {t("commonUi.savedFilters", {
                          ns: "common",
                          defaultValue: "-Saved Filters-",
                        })}
                      </span>
                    </div>

                    {(Array.isArray(showSavedFilter) ? showSavedFilter : []).map(
                      (f, index) => {
                        const isSelected = String(f.id) === String(selectedSavedFilter);
                        return (
                          <div
                            key={f.id ?? index}
                            className={
                              "saved-filter-item" +
                              (isSelected ? " active" : "")
                            }
                            onClick={() => handleSelectSavedFilter(f.id)}
                          >
                            <Tooltip
                              content={f.recentFilterName}
                              placement="right"
                              onlyWhenTruncated
                              ellipsis
                              className="w-100"
                            >
                              {f.recentFilterName}
                            </Tooltip>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </div>
            )}
            {showSearch && (
              <>
                <FormEnter
                  {...(serverSearch ? { onSubmit: handleSearch } : {})}
                >
                  <div
                    className={
                      "searchInput" +
                      (filterMobActive || searchVal !== "" ? " active" : "") +
                      (isSearchDisabled ? " disabled" : "")
                    }
                  >
                    <input
                      type="text"
                      placeholder={t("commonUi.search", {
                        ns: "common",
                        defaultValue: "Search...",
                      })}
                      value={searchVal}
                      onChange={(e) =>
                        setSearchVal
                          ? setSearchVal(e.target.value)
                          : handleSearch(e)
                      }
                      onBlur={handleBlur}
                      list={saveSearchKey}
                      ref={inputRef}
                      disabled={isSearchDisabled}
                    />
                    {saveSearchKey && (
                      <datalist id={saveSearchKey}>
                        {getSearch(saveSearchKey).map((value) => {
                          return <option value={value} key={value}></option>;
                        })}
                      </datalist>
                    )}
                    <button
                      type="submit"
                      onClick={handleFilterActive}
                      disabled={isSearchDisabled}
                    >
                      <CiSearch
                        className={"fs-25" + (name ? " text-white" : "")}
                      />
                    </button>
                  </div>
                </FormEnter>
                {saveFilterKey && (
                  <div
                    className="saveSearch cursor-pointer"
                    onClick={handleOpen}
                    ref={clickRef}
                  >
                    {!savedSearch && (
                      <AppIcon name="save" size={18} className="noShrink" alt="" />
                    )}
                    {savedSearch && <FaCheck className={"fs-20 " + (name ? "text-white" : "text-blue")} />}
                    {!savedSearch && (
                      <span>
                        {t("commonUi.saveFilter", {
                          ns: "common",
                          defaultValue: "Save Filter",
                        })}
                      </span>
                    )}
                    {savedSearch && (
                      <span>
                        {t("commonUi.savedFilter", {
                          ns: "common",
                          defaultValue: "Saved Filter",
                        })}
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
            {customChildrenPlacement === "last" && (
              <div
                className="custom | d-flex align-items-center"
                style={customChildrenStyle}
              >
                {customChildren}
              </div>
            )}
          </div>
        </div>
        {showModal && (
          <SaveFilter
            handleSaveClick={handleSaveClick}
            handleCancelClick={handleClose}
          />
        )}
        {children}
      </div>
    </>
  );
};

export default TableWithHeader;
